/** Bounded list-room rejoin coordinator. REST recovery is notified once per cycle. */

const boundedPositiveInteger = (value, fallback, minimum, maximum) => {
  const parsed = Number(value);
  return Number.isFinite(parsed)
    ? Math.max(minimum, Math.min(maximum, Math.floor(parsed)))
    : fallback;
};

const subscriptionHealthy = result => (
  result?.outcome === 'ack' && result?.ack?.status === true
);

export function createSportsnewListSubscriptionRecovery({
  attempt,
  isRequestCurrent = () => true,
  onFirstFailure,
  onSuccess,
  setTimer = setTimeout,
  clearTimer = clearTimeout,
  baseDelayMs = 500,
  maxDelayMs = 5000,
  maxAttempts = 4,
} = {}) {
  const baseDelay = boundedPositiveInteger(baseDelayMs, 500, 100, 10000);
  const maxDelay = boundedPositiveInteger(maxDelayMs, 5000, baseDelay, 30000);
  const attemptLimit = boundedPositiveInteger(maxAttempts, 4, 1, 8);
  let activeRequest = null;
  let attemptToken = 0;
  let attemptCount = 0;
  let retryTimer = null;
  let ackFence = null;
  let failureNotified = false;
  let joined = false;
  let cycleTrigger = 'activate';
  let cycleRequiredRequestSequence = null;
  let cycleCatchupScheduled = false;

  const cycleContext = () => Object.freeze({
    trigger: cycleTrigger,
    requiredRequestSequence: cycleRequiredRequestSequence,
    catchupScheduled: cycleCatchupScheduled,
  });

  const clearPending = () => {
    if (retryTimer != null) clearTimer(retryTimer);
    retryTimer = null;
    ackFence?.cancel?.();
    ackFence = null;
  };

  const current = (request, token = attemptToken) => (
    token === attemptToken
    && request === activeRequest
    && isRequestCurrent(request)
  );

  const notifyFailure = (request, result) => {
    if (failureNotified) return;
    failureNotified = true;
    if (onFirstFailure?.(request, result, cycleContext()) === true) {
      cycleCatchupScheduled = true;
    }
  };

  const runAttempt = () => {
    const request = activeRequest;
    if (!request || !isRequestCurrent(request) || typeof attempt !== 'function') return false;
    if (retryTimer != null) clearTimer(retryTimer);
    retryTimer = null;
    const token = ++attemptToken;
    attemptCount += 1;
    let settledSynchronously = false;
    const settle = (result) => {
      settledSynchronously = true;
      if (!current(request, token)) return false;
      ackFence = null;
      if (subscriptionHealthy(result)) {
        joined = true;
        attemptCount = 0;
        failureNotified = false;
        if (onSuccess?.(request, result, cycleContext()) === true) {
          cycleCatchupScheduled = true;
        }
        return true;
      }
      joined = false;
      notifyFailure(request, result);
      if (attemptCount < attemptLimit) {
        const delay = Math.min(maxDelay, baseDelay * (2 ** Math.max(0, attemptCount - 1)));
        retryTimer = setTimer(runAttempt, delay);
      }
      return true;
    };
    const nextFence = attempt(request, {
      isCurrent: () => current(request, token),
      onSettled: settle,
    });
    if (!settledSynchronously && current(request, token)) ackFence = nextFence;
    return true;
  };

  return Object.freeze({
    activate(request) {
      clearPending();
      attemptToken += 1;
      activeRequest = request ?? null;
      attemptCount = 0;
      failureNotified = false;
      joined = false;
      cycleTrigger = 'activate';
      cycleRequiredRequestSequence = request?.options?.requiredRequestSequence ?? null;
      cycleCatchupScheduled = false;
      return runAttempt();
    },
    markUnhealthy(result = { outcome: 'heartbeat' }, options = {}) {
      const request = activeRequest;
      if (!request || !isRequestCurrent(request)) return false;
      clearPending();
      attemptToken += 1;
      attemptCount = 0;
      failureNotified = false;
      joined = false;
      cycleTrigger = 'unhealthy';
      cycleRequiredRequestSequence = options.requiredRequestSequence
        ?? request?.options?.requiredRequestSequence
        ?? null;
      cycleCatchupScheduled = false;
      notifyFailure(request, result);
      return runAttempt();
    },
    cancel() {
      clearPending();
      attemptToken += 1;
      activeRequest = null;
      attemptCount = 0;
      failureNotified = false;
      joined = false;
      cycleTrigger = 'activate';
      cycleRequiredRequestSequence = null;
      cycleCatchupScheduled = false;
    },
    state: () => Object.freeze({
      active: activeRequest != null,
      attemptCount,
      retryScheduled: retryTimer != null,
      joined,
      requiredRequestSequence: cycleRequiredRequestSequence,
      catchupScheduled: cycleCatchupScheduled,
    }),
  });
}

export default Object.freeze({ createSportsnewListSubscriptionRecovery });
